import { KButton, KSpan, KSelectRange } from "kahuna-base-react-components"
import React, { useEffect, useRef, useState } from "react"
import { RootState } from "../../../store"
import { connect } from "react-redux"
import { fetchAllSummaries, fetchFilterOptions } from "../../../actions/analysisActions"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { AnalysisSummary } from "../../../types"
import { analysisFilterOptionsKDropdown, platformIcons } from "../../../constants"
//@ts-ignore
import { MultiValue } from "react-select"
import {
  addCommasToNumber,
  formatDateObject,
  getCurrency,
  getSummaries,
  mapListToCamelCase,
  subOptionsSetter,
  platformLogos
} from "../../../utility"
import { ChartOptions } from "chart.js"
import { Bar } from "react-chartjs-2"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"
import { lang } from "../../../constants/languages"

interface PlatformChartData {
  label: string
  icon: string
  totalNet: number
  iconLabel?: string
  label2: string
}

const defaultPlatforms = [
  {label: "YouTube - Audio Tier", label2: "YouTube - Audio Tier", icon: platformIcons["YouTube - Audio Tier"]},
  {label: "Apple Music", label2: "apl-apple", icon: platformIcons["apl-apple"]},
  {label: "Meta Instagram", label2: "Meta-instagram", icon: platformIcons["Meta-instagram"]},
  {label: "TikTok", label2: "tiktok", icon: platformIcons["tiktok"]},
  {label: "Meta Facebook", label2: "Metafbk-facebook", icon: platformIcons["Metafbk-facebook"]},
  {label: "Deezer", label2: "dzr-deezer", icon: platformIcons["dzr-deezer"]}
]

const PlatformChart = ({
  fetchFilterOptions,
  filterOptions,
  initialData,
  currency,
  monthRange,
  defaultStartDate,
  defaultEndDate
}: {
  fetchFilterOptions: () => Promise<void>
  filterOptions?: { [key: string]: KSelectOption[] }
  initialData: AnalysisSummary[]
  currency: string
  monthRange: number
  defaultStartDate: Date
  defaultEndDate: Date
}) => {
  const [selectedSubOptions, setSelectedSubOptions] = useState<KSelectOption | MultiValue<KSelectOption> | undefined>(
    [] as KSelectOption[]
  )
  const [subOptions, setSubOptions] = useState<KSelectOption[]>([])
  const [platforms, setPlatforms] = useState<KSelectOption[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<AnalysisSummary[]>(initialData)
  const [chartData, setChartData] = useState<PlatformChartData[]>([] as PlatformChartData[])
  const [dateValues, setDateValues] = useState<DateRangeType>([
    defaultStartDate,
    defaultEndDate
  ])
  const [approvedDateValues, setApprovedDateValues] = useState<DateRangeType>([null, null])

  const hoveredIndexRef = useRef<number>(-1)
  const [viewMore, setViewMore] = useState<boolean>(false)


  useEffect(() => {
    const subOptionSetting = async () => {
      if (filterOptions) {
        setLoading(true)
        await subOptionsSetter(analysisFilterOptionsKDropdown[2], filterOptions, setSubOptions)
        setLoading(false)
      }
    }
    const platformsSetting = async () => {
      if (!filterOptions) return
      if (filterOptions) {
        setLoading(true)
        await subOptionsSetter(analysisFilterOptionsKDropdown[0], filterOptions, setPlatforms)
        setLoading(false)
      }
    }
    if (filterOptions) {
      platformsSetting()
    }
  }, [filterOptions])

  useEffect(() => {
    const getAllSummary = async () => {
      let responseData
      if (approvedDateValues && approvedDateValues[0] && approvedDateValues[1]) {
        const startDate = formatDateObject(approvedDateValues[0])
        const endDate = formatDateObject(approvedDateValues[1])

        // This will show the specific month, not a range, so start and end will be the same.
        responseData = await getSummaries("platform", "all_platforms", false, startDate, endDate)
        setData(mapListToCamelCase(responseData))
      }
    }

    getAllSummary().then(() => {})
  }, [approvedDateValues])

  useEffect(() => {
    if (initialData) {
      setData(initialData)
    }
  }, [initialData])

  useEffect(() => {
    if (!data) return
    if (data && platforms.length > 0) {
      const chartDataArray: { label: string; icon: string; totalNet: number; iconLabel?: string, label2: string }[] = []
      platforms.forEach((option) => {
        const totalNetResult = data
          .filter((summary) => summary.label === option.value2)
          .reduce((sum, currentValue) => {
            sum += Number(currentValue.totalNet)
            return sum
          }, 0)
        chartDataArray.push({
          label: option.label,
          icon: option.icon || "",
          totalNet: totalNetResult,
          iconLabel: option.iconLabel || option.icon,
          label2: option.value2 || ""
        })
      })
      if (chartDataArray.length < 6) {
        const existingLabels = new Set(chartDataArray.map(((item) => item.label)))

        defaultPlatforms.forEach((defaultPlatform) => {
          if (chartDataArray.length >= 6) return 
          if (!existingLabels.has(defaultPlatform.label)) {
            chartDataArray.push({
              label: defaultPlatform.label,
              label2: defaultPlatform.label2,
              icon: defaultPlatform.icon,
              totalNet: 0
            })
          }
        })

      }
      const sortedChartDataArray = chartDataArray.sort((a, b) => b.totalNet - a.totalNet)
      setChartData(sortedChartDataArray)
    }
  }, [data, platforms])

  const myChartData = {
    labels: viewMore ? chartData.map((data) => data.label) : chartData.slice(0, 6).map((data) => data.label),
    datasets: [
      {
        label: lang.analytics.revenue,
        data: viewMore
          ? chartData.map((data) => Number(data.totalNet.toFixed(2)))
          : chartData.slice(0, 6).map((data) => Number(data.totalNet.toFixed(2))),
        backgroundColor: "#56E099",
        hoverBackgroundColor: "#56E099", // Disable hover color
        hoverBorderWidth: 0, // Disable hover border width
        yAxisID: "y",
        borderSkipped: false,
        barThickness: 16
      }
    ]
  }

  const chartRef = useRef<any>(null)
  const imagesRef = useRef<HTMLImageElement[]>([]) // Store the loaded image in a ref

  useEffect(() => {
    // Preload the images once when the component mounts
    const loadImages = () => {
      const promises = chartData.map((data, index) => {
        return new Promise((resolve) => {
          const img = new Image()
          img.src = platformLogos[data?.label2] || data.icon
          img.onload = () => {
            imagesRef.current[index] = img // Store each image in the ref array
            resolve(img)
          }
        })
      })

      Promise.all(promises).then(() => {
        if (chartRef.current) {
          chartRef.current.update() // Trigger chart update after all images have loaded
        }
      })
    }
    if (chartData.length > 0) {
      loadImages()
    }
  }, [chartData])

  const imagePlugin = {
    id: "imagePlugin",
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      const yAxis = chart.scales.y

      yAxis.ticks.forEach((tick, index) => {
        const y = yAxis.getPixelForTick(index)
        const x = yAxis.left - 28

        const image = imagesRef.current[index]
        if (image) {
          if (tick.label === "Spotify") {
            ctx.drawImage(image, x, y - 12, 80, 24.0135478)
          } else {
            ctx.drawImage(image, x, y - 11, 22, 22)
          }
        } else {
        }
      })
    }
  }

  const circlePlugin = {
    id: "circlePlugin",
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx
      const meta = chart.getDatasetMeta(0) // Assuming we are working with the first dataset

      // Define circle properties
      const innerRadius = 4 // Inner radius of the ring
      const outerRadius = 6 // Outer radius of the ring
      const ringColor = "#FFF" // Color of the ring

      meta.data.forEach((bar, index) => {
        const x = bar.x // X position of the bar
        const y = bar.y // Y position of the bar
        const width = bar.width
        // Draw a circle only on the hovered bar
        if (hoveredIndexRef.current === index && width > 16) {
          ctx.beginPath()
          ctx.fillStyle = ringColor
          ctx.arc(x - 8, y, outerRadius, 0, Math.PI * 2, false) // outer (filled)
          ctx.arc(x - 8, y, innerRadius, 0, Math.PI * 2, true) // outer (filled)
          ctx.fill()
        }
      })
    }
  }

  const options: ChartOptions<"bar"> = {
    onHover: (event, elements, chart) => {
      if (elements.length > 0) {
        const index = elements[0].index
        hoveredIndexRef.current = index
        if (chartRef.current) {
          chartRef.current.update() // Update the chart to apply changes
        }
      } else {
        hoveredIndexRef.current = -1
        if (chartRef.current) {
          chartRef.current.update() // Update the chart to apply changes
        }
      }
      
    },
    responsive: true,
    animation: {
      duration:1000 // default is 1000
    },
    maintainAspectRatio: false,
    indexAxis: "y",
    layout: {
      padding: {
        left: 40
      }
    },
    plugins: {
      legend: {
        display: false,
        labels: {
          color: "#000",
          font: {
            family: "Inter",
            weight: 500,
            size: 13,
            lineHeight: "13px"
          }
        }
      },
      datalabels: {
        display: false
      },
      tooltip: {
        displayColors: false,
        caretSize: 0,
        caretPadding: 10,
        xAlign: "center",
        yAlign: "bottom",
        backgroundColor: "#000",
        bodyFont: {
          family: "Inter",
          weight: 400,
          size: 14,
          lineHeight: "20px"
        },
        callbacks: {
          label: (context) => {
            //let label = context.dataset.label || ""
            const sign = getCurrency(currency)

            if (context.parsed.y !== null) {
              //label += ` ${sign}${addCommasToNumber(context.parsed.y.toFixed(2))}` It can be like this but below one is more reliable
              const label = ` ${sign}${addCommasToNumber(Number(chartData[context.dataIndex].totalNet).toFixed(2))}`
              return label
            } else {
              return ""
            }
          },

          title: (context) => ""
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: "rgba(183, 183, 183, 0.30)",
          drawTicks: false
        },
        border: {
          display: false,
          dash: [3, 3]
        },
        ticks: {
          display: true,
          color: "#999",
          font: {
            family: "Inter",
            weight: 500,
            size: 11,
            lineHeight: "12px"
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        border: {
          display: false,
          color: "#999"
        },
        ticks: {
          display: true,
          color:  chartData.map((data, index) => 
            data.label === "Spotify" ? "transparent" : "#000"
          ),
          crossAlign: "far",
          padding: 10,
          font: {
            family: "Inter",
            size: 14,
            weight: 500,
            lineHeight: "20px"
          }
        }
      }
    },
    elements: {
      bar: {
        borderRadius: {
          bottomLeft: 6,
          bottomRight: 6,
          topLeft: 6,
          topRight: 6
        }
      }
    }
  }

  return (
    <div
      className="w-full p-5 flex flex-col justify-start gap-3"
      style={{
        borderRadius: "10px",
        border: "1px solid var(--stroke-soft-200, #F3F3F3)",
        background: " var(--bg-white-0, #FFF)",
        boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
      }}
    >
      <div className="flex flex-row justify-between">
        <div className="flex flex-row gap-1 items-center pl-2.5">
          <img src="/analytics_icons/link.svg" alt="link-icon" />
          <KSpan text={lang.analytics.listened_platform} color="#000" fontWeight={500} />
        </div>
        <div className="flex flex-row gap-1">
          <div>
            <KSelectRange
              value={dateValues}
              onChange={(event) => {
                if (
                  event &&
                  dateValues &&
                  (event[0]?.getTime() !== dateValues[0]?.getTime() || event[1]?.getTime() !== dateValues[1]?.getTime())
                ) {
                  setDateValues(event)
                  setApprovedDateValues(event)
                }
              }}
            />
          </div>
        </div>
      </div>
      <div className="grow flex flex-row items-center w-full">
        {chartData && chartData.length > 0 && (
          <Bar
            data={myChartData}
            options={options}
            plugins={[imagePlugin, circlePlugin]}
            style={{
              height: "450px",
              width: "100%"
            }}
          />
        )}
      </div>
      <div className="flex items-center justify-center">
        <KButton
          text={viewMore ? lang.analytics.view_less_button : lang.analytics.view_more_button}
          background="#FFF"
          height="32px"
          width="auto"
          onClick={() => setViewMore(!viewMore)}
        />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    filterOptions: state.analysis.filterOptions,
    allSummaries: state.analysis.allSummaries,
    currency: state.auth.user.preferredCurrency
  }
}

export default connect(mapStateToProps, { fetchFilterOptions, fetchAllSummaries })(PlatformChart)
